import React from "react";
import { Helmet } from "react-helmet";
import SEOHead from "../components/SEOHead";
import { Link } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import {
  withStyles,
  createMuiTheme,
  MuiThemeProvider,
} from "@material-ui/core/styles";
import localizationManager from "./localizationManager";

const theme = createMuiTheme({
  typography: {
    useNextVariants: true,
  },
  palette: {
    primary: {
      main: "#2196f3",
    },
    secondary: {
      main: "#E44A4B",
    },
  },
});

const styles = (theme) => ({
  root: {
    marginTop: 80,
    padding: `${theme.spacing.unit * 4}px`,
  },
  content: {
    maxWidth: 960,
    margin: "0 auto",
  },
  section: {
    marginBottom: theme.spacing.unit * 5,
  },
  h1: {
    fontWeight: 500,
    marginBottom: theme.spacing.unit * 3,
  },
  h2: {
    fontWeight: 500,
    marginTop: theme.spacing.unit * 4,
    marginBottom: theme.spacing.unit * 2,
  },
  paragraph: {
    lineHeight: 1.8,
    marginBottom: theme.spacing.unit * 2,
  },
  ctaContainer: {
    marginTop: theme.spacing.unit * 2,
    display: "flex",
    gap: theme.spacing.unit * 2,
    flexWrap: "wrap",
  },
});

function TaiwanInverterManufacturer(props) {
  const { classes } = props;
  const lang = props.match && props.match.params && props.match.params.lang;
  const prefix = lang ? `/${lang}` : "";

  const structuredDataWebPage = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": localizationManager.getLocalStringPlain("@@seo_taiwan_inverter_title"),
    "description": localizationManager.getLocalStringPlain("@@seo_taiwan_inverter_description"),
    "url": `https://www.dc-ac.com${prefix}/taiwan-inverter-manufacturer`,
    "inLanguage": lang || "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "LinkChamp Co., Ltd.",
      "url": "https://www.dc-ac.com/"
    },
    "about": {
      "@type": "Organization",
      "@id": "https://www.dc-ac.com/#organization",
      "name": "LinkChamp Co., Ltd.",
      "url": "https://www.dc-ac.com/",
      "description": localizationManager.getLocalStringPlain("@@company_identity_statement"),
      "foundingDate": "1993",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "No. 28, Lane 687, ZhongShan Rd., Rende Dist.",
        "addressLocality": "Tainan City",
        "postalCode": "71742",
        "addressCountry": "TW"
      }
    }
  });

  return (
    <MuiThemeProvider theme={theme}>
      <React.Fragment>
        <CssBaseline />
        <main className={classes.root}>
          <SEOHead
            title={localizationManager.getLocalStringPlain(
              "@@seo_taiwan_inverter_title",
            )}
            description={localizationManager.getLocalStringPlain(
              "@@seo_taiwan_inverter_description",
            )}
            location={props.location}
            lang={localizationManager.currentLang}
          />
          <Helmet>
            <meta property="og:type" content="website" />
            <meta
              property="og:title"
              content={localizationManager.getLocalStringPlain(
                "@@seo_taiwan_inverter_title",
              )}
            />
            <meta
              property="og:description"
              content={localizationManager.getLocalStringPlain(
                "@@seo_taiwan_inverter_description",
              )}
            />
            <meta
              property="og:image"
              content="https://www.dc-ac.com/images/LinkChamp-OG-Image.png"
            />
            <script type="application/ld+json">
              {structuredDataWebPage}
            </script>
          </Helmet>

          <article
            className={classes.content}
            itemScope
            itemType="https://schema.org/Organization"
          >
            <meta itemProp="name" content="LinkChamp Co., Ltd." />
            <meta itemProp="url" content="https://www.dc-ac.com/" />

            <Typography
              component="h1"
              variant="h4"
              className={classes.h1}
              color="textPrimary"
            >
              {localizationManager.getLocalString("@@taiwan_inverter_h1")}
            </Typography>

            {/* Section 1: About */}
            <section className={classes.section}>
              <Typography
                component="h2"
                variant="h5"
                className={classes.h2}
                color="textPrimary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_about_title")}
              </Typography>
              <Typography
                component="p"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_about_content")}
              </Typography>
            </section>

            {/* Section 2: Product Categories */}
            <section className={classes.section}>
              <Typography
                component="h2"
                variant="h5"
                className={classes.h2}
                color="textPrimary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_categories_title")}
              </Typography>
              <Typography
                component="p"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_categories_content")}
              </Typography>
              <Typography
                component="div"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                <ul style={{ lineHeight: 2.2, paddingLeft: 20 }}>
                  <li>
                    <strong>{localizationManager.getLocalString("@@taiwan_inverter_cat_psw_title")}</strong>
                    {localizationManager.getLocalString("@@taiwan_inverter_cat_psw_text")}
                    <Link to={`${prefix}/product/sn-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_psw_series")}
                    </Link>
                  </li>
                  <li>
                    <strong>{localizationManager.getLocalString("@@taiwan_inverter_cat_chg_title")}</strong>
                    {localizationManager.getLocalString("@@taiwan_inverter_cat_chg_text")}
                    <Link to={`${prefix}/product/sn-c-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_chg_series")}
                    </Link>
                  </li>
                  <li>
                    <strong>{localizationManager.getLocalString("@@taiwan_inverter_cat_mod_title")}</strong>
                    {localizationManager.getLocalString("@@taiwan_inverter_cat_mod_text")}
                    <Link to={`${prefix}/product/sp-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_mod_series_sp")}
                    </Link>
                    {", "}
                    <Link to={`${prefix}/product/hp-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_mod_series_hp")}
                    </Link>
                  </li>
                  <li>
                    <strong>{localizationManager.getLocalString("@@taiwan_inverter_cat_conv_title")}</strong>
                    {localizationManager.getLocalString("@@taiwan_inverter_cat_conv_text")}
                    <Link to={`${prefix}/product/dc-1224-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_conv_series_1224")}
                    </Link>
                    {", "}
                    <Link to={`${prefix}/product/dc-2412-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_conv_series_2412")}
                    </Link>
                    {", "}
                    <Link to={`${prefix}/product/dc-48-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_conv_series_48")}
                    </Link>
                    {", "}
                    <Link to={`${prefix}/product/dc-4in1-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_conv_series_4in1")}
                    </Link>
                  </li>
                  <li>
                    <strong>{localizationManager.getLocalString("@@taiwan_inverter_cat_bat_title")}</strong>
                    {localizationManager.getLocalString("@@taiwan_inverter_cat_bat_text")}
                    <Link to={`${prefix}/product/ad-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_bat_series_ad")}
                    </Link>
                    {", "}
                    <Link to={`${prefix}/product/dcc-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_bat_series_dcc")}
                    </Link>
                  </li>
                  <li>
                    <strong>{localizationManager.getLocalString("@@taiwan_inverter_cat_acc_title")}</strong>
                    {localizationManager.getLocalString("@@taiwan_inverter_cat_acc_text")}
                    <Link to={`${prefix}/product/rc-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_acc_series_rc")}
                    </Link>
                    {", "}
                    <Link to={`${prefix}/product/ats-series`} style={{ textDecoration: 'none', color: '#E44A4B' }}>
                      {localizationManager.getLocalString("@@taiwan_inverter_cat_acc_series_ats")}
                    </Link>
                  </li>
                </ul>
              </Typography>
            </section>

            {/* Section 3: Manufacturing */}
            <section className={classes.section}>
              <Typography
                component="h2"
                variant="h5"
                className={classes.h2}
                color="textPrimary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_mfg_title")}
              </Typography>
              <Typography
                component="p"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_mfg_content")}
              </Typography>
            </section>

            {/* Section 4: OEM/ODM */}
            <section className={classes.section}>
              <Typography
                component="h2"
                variant="h5"
                className={classes.h2}
                color="textPrimary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_oem_title")}
              </Typography>
              <Typography
                component="p"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_oem_content")}
              </Typography>
            </section>

            {/* Section 5: Export Markets */}
            <section className={classes.section}>
              <Typography
                component="h2"
                variant="h5"
                className={classes.h2}
                color="textPrimary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_export_title")}
              </Typography>
              <Typography
                component="p"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_export_content")}
              </Typography>
            </section>

            {/* Section 6: Why Taiwan */}
            <section className={classes.section}>
              <Typography
                component="h2"
                variant="h5"
                className={classes.h2}
                color="textPrimary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_why_title")}
              </Typography>
              <Typography
                component="p"
                variant="body1"
                className={classes.paragraph}
                color="textSecondary"
              >
                {localizationManager.getLocalString("@@taiwan_inverter_why_content")}
              </Typography>
            </section>

            {/* CTA */}
            <div className={classes.ctaContainer}>
              <Button
                variant="outlined"
                color="secondary"
                component={Link}
                to={`${prefix}/company`}
                style={{ marginRight: 16 }}
              >
                {localizationManager.getLocalString("@@btn_about_company")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                component={Link}
                to={`${prefix}/product`}
                style={{ marginRight: 16 }}
              >
                {localizationManager.getLocalString("@@btn_view_products")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                component={Link}
                to={`${prefix}/contact`}
                style={{ marginRight: 16 }}
              >
                {localizationManager.getLocalString("@@btn_contact_linkchamp")}
              </Button>
            </div>
          </article>
        </main>
      </React.Fragment>
    </MuiThemeProvider>
  );
}

export default withStyles(styles)(TaiwanInverterManufacturer);
